import { Card, CardContent, CardDescription, CardHeader, CardTitle, Separator } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatusChip } from '@/components/ui/status-chip';
import { formatBrokerDateTime } from '@/features/brokers/batches/batch-formatters';
import { useBrokerTrades, useBrokerTradingStats } from '@/hooks/queries/brokers/use-broker-detail';
import type { FeeSplit, TradingStatsPeriod, WireBrokerTrade } from '@/services/api/brokers';
import { formatUsd } from '@/utils/format';
import { brokerOtSplitLabel, CHIP_XS_CLASS, feeSplitSummary, formatDateShort, formatNumber } from './broker-detail-format';
import { ErrorPanel, LoadMoreButton } from './detail-panels';

const StatRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between gap-4 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium tabular-nums">{value}</span>
  </div>
);
StatRow.displayName = 'StatRow';

const SectionLabel = ({ children }: { children: string }) => <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</span>;
SectionLabel.displayName = 'SectionLabel';

interface PeriodCardProps {
  title: string;
  period: TradingStatsPeriod;
  emptyFeesMessage: string;
}

const PeriodCard = ({ title, period, emptyFeesMessage }: PeriodCardProps) => (
  <Card>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      <CardDescription>
        {formatDateShort(period.from)} – {formatDateShort(period.to)}
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col gap-3">
      <StatRow label="Volume (USD)" value={formatUsd(period.volume_usd)} />
      <StatRow label="Trades" value={period.trade_count.toLocaleString()} />
      <Separator />
      <SectionLabel>Total Fees</SectionLabel>
      {period.fees.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyFeesMessage}</p>
      ) : (
        period.fees.map((fee) => <StatRow key={fee.currency} label={fee.currency} value={formatNumber(fee.amount)} />)
      )}
    </CardContent>
  </Card>
);
PeriodCard.displayName = 'PeriodCard';

/** trading-stats sends `fee_split: null` (vs omitted on the broker object) — handle both. */
const FeeSplitCard = ({ feeSplit }: { feeSplit: FeeSplit | null | undefined }) => (
  <Card>
    <CardHeader>
      <CardTitle>Fee Split</CardTitle>
      <CardDescription>{feeSplit ? feeSplitSummary(feeSplit) : 'Not configured'}</CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col gap-3">
      {feeSplit ? (
        <>
          <SectionLabel>Configured Rates</SectionLabel>
          {feeSplit.type === 'percentage' ? (
            <>
              <StatRow label="Trades" value={brokerOtSplitLabel(feeSplit.trades)} />
              <StatRow label="Liquidations" value={brokerOtSplitLabel(feeSplit.liquidations)} />
            </>
          ) : (
            <>
              <StatRow label="OT Maker Fee" value={`${feeSplit.maker_fee_bps} bps`} />
              <StatRow label="OT Taker Fee" value={`${feeSplit.taker_fee_bps} bps`} />
              <StatRow label="Liquidations" value={brokerOtSplitLabel(feeSplit.liquidations)} />
            </>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Configure a fee split in Overview to see rates.</p>
      )}
    </CardContent>
  </Card>
);
FeeSplitCard.displayName = 'FeeSplitCard';

// Module-level columns — stable reference for DataTable.
const TRADE_COLUMNS: ColumnDef<WireBrokerTrade, unknown>[] = [
  { accessorKey: 'timestamp', header: 'Timestamp', cell: ({ row }) => <DateCell format={formatBrokerDateTime} value={row.original.timestamp} /> },
  { accessorKey: 'instrument', header: 'Instrument' },
  {
    accessorKey: 'side',
    header: 'Side',
    cell: ({ row }) => (
      <StatusChip className={CHIP_XS_CLASS} variant={row.original.side === 'BUY' ? 'success' : 'destructive'}>
        {row.original.side}
      </StatusChip>
    ),
  },
  { accessorKey: 'price', header: 'Price', cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.price)}</span> },
  {
    // Intentionally `matched_amount`, NOT `amount` — matched fill is the meaningful
    // quantity; do not "fix" this when touching the port
    accessorKey: 'matched_amount',
    header: 'Amount',
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.matched_amount)}</span>,
  },
  { accessorKey: 'notional', header: 'Notional', cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.notional)}</span> },
  {
    accessorKey: 'fee',
    header: 'Fee',
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatNumber(row.original.fee)} {row.original.fee_currency}
      </span>
    ),
  },
  {
    accessorKey: 'user_fast_id',
    header: 'User',
    cell: ({ row }) => {
      const userFastId = row.original.user_fast_id;
      if (!userFastId) return <span className="text-muted-foreground">—</span>;
      // FAST id linked into the same route the Clients tab feeds UUIDs — legacy user
      // pages accepted both id spaces; emitted for parity until the v2 user console
      // lands
      return (
        <Link className="font-mono text-xs text-blue-500 hover:underline" to={`/operations/users/${userFastId}`}>
          {userFastId}
        </Link>
      );
    },
  },
  { accessorKey: 'trade_id', header: 'Trade ID', cell: ({ row }) => <span className="font-mono text-xs">{row.original.trade_id}</span> },
];

interface TradesTabProps {
  brokerId: string;
}

/**
 * Both queries mount together → trading-stats AND trades load in parallel (legacy
 * Promise.all). Deliberate fix: the legacy tab gated
 * everything behind the ~29 s stats fetch, so a stats failure blanked the whole tab
 * — here the stats grid and the Recent Trades card load and fail independently.
 */
export const TradesTab = ({ brokerId }: TradesTabProps) => {
  const statsQuery = useBrokerTradingStats(brokerId);
  const tradesQuery = useBrokerTrades(brokerId);

  return (
    <div className="flex flex-col gap-4">
      {statsQuery.isPending ? (
        <LoadingPanel />
      ) : statsQuery.isError ? (
        <ErrorPanel error={statsQuery.error} onRetry={() => statsQuery.refetch()} title="Failed to fetch trading stats" />
      ) : (
        <div className="grid max-w-[1200px] grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4">
          <PeriodCard emptyFeesMessage="No fees this month." period={statsQuery.data.month} title="This Month" />
          <PeriodCard emptyFeesMessage="No fees this week." period={statsQuery.data.week} title="This Week" />
          <FeeSplitCard feeSplit={statsQuery.data.fee_split} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent Trades</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {/* Full panel only when nothing loaded; a failed Load More keeps the
              table mounted with its rows and surfaces the error inline below */}
          {tradesQuery.isError && !tradesQuery.data ? (
            <ErrorPanel error={tradesQuery.error} onRetry={() => tradesQuery.refetch()} title="Failed to fetch trades" />
          ) : (
            <>
              <DataTable
                columns={TRADE_COLUMNS}
                data={tradesQuery.data}
                emptyMessage="No trades found."
                getRowId={(row) => row.trade_id}
                isLoading={tradesQuery.isPending}
                surface={false}
              />
              {tradesQuery.isError ? (
                <ErrorPanel error={tradesQuery.error} onRetry={() => tradesQuery.refetch()} title="Failed to fetch trades" />
              ) : (
                <LoadMoreButton hasNextPage={tradesQuery.hasNextPage} isFetchingNextPage={tradesQuery.isFetchingNextPage} onLoadMore={() => tradesQuery.fetchNextPage()} />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
TradesTab.displayName = 'TradesTab';
